package com.schoolconnect.app.feature.studentenrollment

import androidx.compose.ui.graphics.Color

enum class StudentEnrollmentWorkflowStage {
    ValidationSummary,
    ReviewAndSave
}

private fun defaultStudentEnrollmentTabs(): List<StudentEnrollmentTabUiModel> = listOf(
    StudentEnrollmentTabUiModel(id = "students", title = "Students", count = 0, isSelected = true),
    StudentEnrollmentTabUiModel(id = "parents", title = "Parents", count = 0)
)

private fun defaultStudentEnrollmentPageIndexByTab(): Map<String, Int> = defaultStudentEnrollmentTabs().associate { it.id to 0 }

data class StudentEnrollmentUiState(
    val pageTitle: String = "Import Student Enrollment",
    val validationSubtitle: String = "Upload and validate student and parent data from Excel before importing to the system.",
    val reviewSubtitle: String = "Review valid records before saving to the system.",
    val contextLine: String = "",
    val backToBulkImportLabel: String = "Back to Bulk Import",
    val backToValidationSummaryLabel: String = "Back to Validation Summary",
    val stage: StudentEnrollmentWorkflowStage = StudentEnrollmentWorkflowStage.ValidationSummary,
    val steps: List<StudentEnrollmentStepUiModel> = listOf(
        StudentEnrollmentStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isActive = true),
        StudentEnrollmentStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save")
    ),
    val existingDetails: List<StudentEnrollmentSummaryRow> = listOf(
        StudentEnrollmentSummaryRow("Students", "0"),
        StudentEnrollmentSummaryRow("Parents", "0")
    ),
    val newImportDetails: List<StudentEnrollmentSummaryRow> = listOf(
        StudentEnrollmentSummaryRow("File Name", "No file selected"),
        StudentEnrollmentSummaryRow("Selected On", "Not selected"),
        StudentEnrollmentSummaryRow("Selected By", "Not selected"),
        StudentEnrollmentSummaryRow("Total Rows", "0")
    ),
    val validationDetails: List<StudentEnrollmentSummaryRow> = listOf(
        StudentEnrollmentSummaryRow("Total Rows", "0"),
        StudentEnrollmentSummaryRow("Valid Rows", "0", valueColor = Color(0xFF16A34A)),
        StudentEnrollmentSummaryRow("Invalid Rows", "0", valueColor = Color(0xFFDC2626)),
        StudentEnrollmentSummaryRow(
            "Status",
            badgeText = "Awaiting Upload",
            badgeBackground = Color(0xFFFFF4D6),
            badgeColor = Color(0xFFB45309)
        )
    ),
    val validationLastUpdated: String = "Upload an XLSX workbook to begin validation",
    val sheetOptions: List<String> = listOf("All Sheets", "Students", "Parents", "Reference Values"),
    val selectedSheet: String = "All Sheets",
    val totalErrorsLabel: String = "Validation Messages",
    val errorHelpText: String = "Upload a workbook to see validation issues or parsed rows.",
    val errorRows: List<StudentEnrollmentErrorRow> = emptyList(),
    val serverValidationPassed: Boolean = false,
    val structureValidationErrors: List<String> = emptyList(),
    val errorPageText: String = "Showing 0 to 0 of 0 errors",
    val pageIndex: Int = 0,
    val pageCount: Int = 1,
    val reviewSummary: StudentEnrollmentReviewSummary = StudentEnrollmentReviewSummary(
        fileName = "No file selected",
        uploadedOn = "Not selected",
        readyToSaveRows = 0,
        status = "Awaiting Upload"
    ),
    val reviewTabs: List<StudentEnrollmentTabUiModel> = defaultStudentEnrollmentTabs(),
    val selectedTabId: String = "students",
    val studentRecords: List<StudentEnrollmentStudentRecordUiModel> = emptyList(),
    val parentRecords: List<StudentEnrollmentParentRecordUiModel> = emptyList(),
    val rowsPerPage: Int = com.schoolconnect.app.feature.common.PaginationPolicy.DEFAULT_PAGE_SIZE,
    val reviewPageIndexByTab: Map<String, Int> = defaultStudentEnrollmentPageIndexByTab(),
    val isLoading: Boolean = false,
    val serverValidationCompleted: Boolean = false,
    val showReviewWarning: Boolean = false,
    val showSaveWarning: Boolean = false,
    val errorMessage: String = ""
)

data class StudentEnrollmentStepUiModel(
    val number: Int,
    val title: String,
    val subtitle: String,
    val isActive: Boolean = false,
    val isCompleted: Boolean = false
)

data class StudentEnrollmentReviewSummary(
    val fileName: String,
    val uploadedOn: String,
    val readyToSaveRows: Int,
    val status: String
)

data class StudentEnrollmentTabUiModel(
    val id: String,
    val title: String,
    val count: Int,
    val isSelected: Boolean = false
)

data class StudentEnrollmentSummaryRow(
    val label: String,
    val value: String = "",
    val valueColor: Color = Color(0xFF0A1446),
    val badgeText: String? = null,
    val badgeBackground: Color = Color.Transparent,
    val badgeColor: Color = Color.Unspecified
)

data class StudentEnrollmentErrorRow(
    val sheetName: String,
    val rowNo: String,
    val message: String,
    val id: String = "$sheetName:$rowNo:$message"
)

data class StudentEnrollmentSubjectChipUiModel(
    val label: String,
    val background: Color,
    val contentColor: Color
)

data class StudentEnrollmentStudentRecordUiModel(
    val rowNo: Int,
    val admissionNo: String,
    val className: String,
    val section: String,
    val studentName: String,
    val rollNo: String,
    val gender: String,
    val dob: String,
    val subjectCodes: List<StudentEnrollmentSubjectChipUiModel>,
    val bloodGroup: String
)

data class StudentEnrollmentParentRecordUiModel(
    val rowNo: Int,
    val admissionNo: String,
    val primaryContactName: String,
    val primaryContactMobile: String,
    val primaryContactEmail: String,
    val primaryContactRelationship: String,
    val secondaryContactName: String,
    val secondaryContactMobile: String,
    val secondaryContactEmail: String,
    val secondaryContactRelationship: String
)

data class StudentEnrollmentWorkbookPreview(
    val fileName: String,
    val uploadedOn: String,
    val studentRecords: List<StudentEnrollmentStudentRecordUiModel>,
    val parentRecords: List<StudentEnrollmentParentRecordUiModel>,
    val errorRows: List<StudentEnrollmentErrorRow>,
    val validationMessages: List<String>,
    val structureErrors: List<String> = emptyList()
)

sealed interface StudentEnrollmentUiEvent {
    data object BackClicked : StudentEnrollmentUiEvent
    data object BackToValidationSummaryClicked : StudentEnrollmentUiEvent
    data object DownloadCurrentDataClicked : StudentEnrollmentUiEvent
    data object DownloadTemplateClicked : StudentEnrollmentUiEvent
    data object UploadFileClicked : StudentEnrollmentUiEvent
    data object ValidateFileClicked : StudentEnrollmentUiEvent
    data object DownloadErrorDetailsClicked : StudentEnrollmentUiEvent
    data object FilterClicked : StudentEnrollmentUiEvent
    data object CancelClicked : StudentEnrollmentUiEvent
    data object ContinueReviewClicked : StudentEnrollmentUiEvent
    data object DismissReviewWarning : StudentEnrollmentUiEvent
    data object ConfirmContinueReviewClicked : StudentEnrollmentUiEvent
    data object DismissSaveWarning : StudentEnrollmentUiEvent
    data object ConfirmSaveRecordsClicked : StudentEnrollmentUiEvent
    data object SaveRecordsClicked : StudentEnrollmentUiEvent
    data object DismissStructureErrors : StudentEnrollmentUiEvent
    data class WorkbookPrepared(val fileName: String, val fileBytes: ByteArray) : StudentEnrollmentUiEvent
    data class WorkbookLoadStarted(val fileName: String) : StudentEnrollmentUiEvent
    data class WorkbookLoaded(val preview: StudentEnrollmentWorkbookPreview) : StudentEnrollmentUiEvent
    data class WorkbookLoadFailed(val message: String) : StudentEnrollmentUiEvent
    data class TabSelected(val tabId: String) : StudentEnrollmentUiEvent
    data class RowsPerPageChanged(val rowsPerPage: Int) : StudentEnrollmentUiEvent
    data class SheetSelected(val sheetName: String) : StudentEnrollmentUiEvent
    data class PageChanged(val pageIndex: Int) : StudentEnrollmentUiEvent
}

private fun generateStudentEnrollmentErrorRows(count: Int): List<StudentEnrollmentErrorRow> {
    val sheetNames = listOf("Students", "Parents")
    val messages = listOf(
        "Invalid DOB format \"31-02-2016\". Please use dd-MMM-yyyy.",
        "Missing Class.",
        "Roll No. \"11\" is already used in Class 1 - A.",
        "Invalid Blood Group \"X\".",
        "Invalid Primary Contact Mobile number \"987654321A\".",
        "Missing Primary Contact Relationship.",
        "Invalid Parent Email address \"deepa.nair@school\".",
        "Duplicate admission number \"ADM021\"."
    )
    return List(count) { index ->
        StudentEnrollmentErrorRow(
            sheetName = sheetNames[index % sheetNames.size],
            rowNo = (index + 1).toString(),
            message = messages[index % messages.size]
        )
    }
}

private fun generateStudentEnrollmentStudentRecords(count: Int): List<StudentEnrollmentStudentRecordUiModel> {
    val names = listOf(
        "Rahul Nair",
        "Ananya Pillai",
        "Arjun Menon",
        "Diya Thomas",
        "Ishaan Verma",
        "Meera Iyer",
        "Karthik Suresh",
        "Sara Joseph",
        "Vivaan Rao",
        "Nandini Krishnan"
    )
    val bloodGroups = listOf("O+", "A+", "B+", "AB+", "O-", "A-", "B-", "AB-")
    val chipPalette = listOf(
        Color(0xFFE2ECFF) to Color(0xFF2563EB),
        Color(0xFFE6F7EE) to Color(0xFF18A765),
        Color(0xFFF0E4FF) to Color(0xFF7C3AED)
    )
    val subjectCodes = listOf("MAT", "SCI", "ENG", "HIN", "SST", "MUS")
    return List(count) { index ->
        val rowNo = index + 1
        val paletteA = chipPalette[index % chipPalette.size]
        val paletteB = chipPalette[(index + 1) % chipPalette.size]
        val paletteC = chipPalette[(index + 2) % chipPalette.size]
        StudentEnrollmentStudentRecordUiModel(
            rowNo = rowNo,
            admissionNo = "ADM${rowNo.toString().padStart(3, '0')}",
            className = ((index % 10) / 2 + 1).toString(),
            section = listOf("A", "B", "C")[(index / 2) % 3],
            studentName = names[index % names.size] + if (rowNo > 10) " ${((rowNo - 1) / 10) + 1}" else "",
            rollNo = ((index % 15) + 1).toString(),
            gender = if (index % 2 == 0) "Male" else "Female",
            dob = listOf(
                "12-Jun-2016",
                "04-Mar-2016",
                "21-Jan-2016",
                "17-Apr-2016",
                "09-Jul-2015",
                "23-Feb-2015",
                "16-May-2015",
                "30-Jun-2015",
                "14-Dec-2014",
                "05-Sep-2014"
            )[index % 10],
            subjectCodes = listOf(
                StudentEnrollmentSubjectChipUiModel("MAT", paletteA.first, paletteA.second),
                StudentEnrollmentSubjectChipUiModel("SCI", paletteB.first, paletteB.second),
                StudentEnrollmentSubjectChipUiModel("ENG", paletteC.first, paletteC.second)
            ),
            bloodGroup = bloodGroups[index % bloodGroups.size]
        )
    }
}

private fun generateStudentEnrollmentParentRecords(count: Int): List<StudentEnrollmentParentRecordUiModel> {
    val primaryNames = listOf(
        "Rajesh Nair",
        "Suresh Pillai",
        "Vineeth Menon",
        "Thomas Varghese",
        "Rohit Verma",
        "Arun Kumar",
        "Naveen Joseph",
        "Jitin Shah",
        "Dinesh Reddy",
        "Manoj Iyer"
    )
    val secondaryNames = listOf(
        "Deepa Nair",
        "Anjali Pillai",
        "Lakshmi Menon",
        "Neethu Varghese",
        "Priya Verma",
        "Swapna Kumar",
        "Sincy Joseph",
        "Meera Shah",
        "Kavya Reddy",
        "Radha Iyer"
    )
    return List(count) { index ->
        val rowNo = index + 1
        StudentEnrollmentParentRecordUiModel(
            rowNo = rowNo,
            admissionNo = "ADM${rowNo.toString().padStart(3, '0')}",
            primaryContactName = primaryNames[index % primaryNames.size] + if (rowNo > 10) " ${((rowNo - 1) / 10) + 1}" else "",
            primaryContactMobile = (9876543210L + rowNo).toString(),
            primaryContactEmail = "parent${rowNo}@example.com",
            primaryContactRelationship = "Father",
            secondaryContactName = secondaryNames[index % secondaryNames.size] + if (rowNo > 10) " ${((rowNo - 1) / 10) + 1}" else "",
            secondaryContactMobile = (9876544210L + rowNo).toString(),
            secondaryContactEmail = "parent${rowNo}.secondary@example.com",
            secondaryContactRelationship = "Mother"
        )
    }
}
